import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { registerDentistAccount } from '../../../services/dentistRegistrationService';

const SignUpForm = ({ onBackToLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (errors.form) {
      setErrors((prev) => ({ ...prev, form: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('signUpForm.fullNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('loginForm.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('loginForm.emailInvalid');
    }
    if (!formData.password) {
      newErrors.password = t('loginForm.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('signUpForm.passwordMinLength');
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('signUpForm.passwordMismatch');
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage(null);

    try {
      const result = await registerDentistAccount({
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        companyName: formData.companyName,
        phone: formData.phone,
      });
      setSuccessMessage(result?.message || t('signUpForm.success'));
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
      });
    } catch (err) {
      setErrors({ form: err.message || t('signUpForm.errorGeneric') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
          style={{
            background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
          }}
        >
          <Icon name="UserPlus" size={32} color="#FFFFFF" />
        </div>
        <h1 className="text-3xl font-semibold text-foreground mb-2">{t('signUpForm.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('signUpForm.subtitle')}</p>
      </div>

      {successMessage ? (
        <div className="space-y-6">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-success/10 border border-success/30">
            <Icon name="CheckCircle2" size={22} className="text-success shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">{successMessage}</p>
          </div>
          <Button type="button" variant="default" size="lg" fullWidth onClick={onBackToLogin}>
            {t('signUpForm.backToLogin')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.form ? (
            <div className="flex items-center gap-2 p-4 rounded-lg bg-error/10 border border-error/20">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{errors.form}</p>
            </div>
          ) : null}

          <Input
            type="text"
            name="fullName"
            label={t('signUpForm.fullName')}
            placeholder={t('signUpForm.fullNamePlaceholder')}
            value={formData.fullName}
            onChange={handleChange}
            error={errors.fullName}
            required
            disabled={loading}
          />

          <Input
            type="text"
            name="companyName"
            label={t('signUpForm.companyName')}
            placeholder={t('signUpForm.companyNamePlaceholder')}
            value={formData.companyName}
            onChange={handleChange}
            disabled={loading}
          />

          <Input
            type="email"
            name="email"
            label={t('loginForm.email')}
            placeholder={t('loginForm.emailPlaceholder')}
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
            disabled={loading}
          />

          <Input
            type="tel"
            name="phone"
            label={t('signUpForm.phone')}
            placeholder={t('signUpForm.phonePlaceholder')}
            value={formData.phone}
            onChange={handleChange}
            disabled={loading}
          />

          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              name="password"
              label={t('loginForm.password')}
              placeholder={t('signUpForm.passwordPlaceholder')}
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon style={{ marginTop: '-26px' }} name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <Input
            type="password"
            name="confirmPassword"
            label={t('signUpForm.confirmPassword')}
            placeholder={t('signUpForm.confirmPasswordPlaceholder')}
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
            required
            disabled={loading}
          />

          <p className="text-xs text-muted-foreground">{t('signUpForm.pendingActivationHint')}</p>

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={loading}
            iconName="UserPlus"
            iconPosition="right"
          >
            {t('signUpForm.submit')}
          </Button>

          <button
            type="button"
            onClick={onBackToLogin}
            className="w-full text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {t('signUpForm.alreadyHaveAccount')}
          </button>
        </form>
      )}
    </div>
  );
};

export default SignUpForm;
